/**
 * ContactCard — directory People-view card (Block 6.F.2 → X.6.5B reskin;
 * TypeScript per UX-13 S2, aligned to the canonical dense card §3).
 *
 * Identity zone: forest initials avatar (a circle — avatars keep their
 * shape per the geometry ruling), Playfair name, role; organization(s)
 * line; free-text specialties; hairline footer with phone (tel: link)
 * left and "Contact →" right. Craft correction per §3: the truncated
 * organization line carries `title` (the never-silently rule). Data
 * shape unchanged.
 */

import { type ReactNode } from 'react'
import { ArrowRight, Phone, Building2 } from 'lucide-react'
import { Chip } from '@/components/primitives/Chip'
import { RecommendedBadge } from '@/components/RecommendedBadge'
import type { IndividualForSearch } from '@/lib/types'

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter((w) => w && /[A-Za-z]/.test(w[0]))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')
}

export interface ContactCardProps {
  contact: IndividualForSearch
  onClick: (contact: IndividualForSearch) => void
}

export function ContactCard({ contact, onClick }: ContactCardProps): ReactNode {
  const {
    full_name, title, current_org_names, business_phone, cell_phone, specialties, is_referral_partner,
    is_recommended, recommended_at, recommended_by_display_name,
  } = contact

  const displayOrgName = current_org_names.length > 0 ? current_org_names.join(' / ') : ''
  const displayPhone = business_phone || cell_phone

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(contact)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(contact) } }}
      className="group flex cursor-pointer flex-col rounded-card bg-paper p-5 shadow-sh2 transition-[box-shadow,transform] duration-[var(--dur-slow)] ease-quiet hover:-translate-y-[3px] hover:shadow-sh3 motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-pill bg-sage-800 font-display text-[16px] font-semibold text-cream">
          {initialsOf(full_name)}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="flex flex-wrap items-center gap-2 font-display text-[16px] font-semibold leading-snug tracking-[-0.01em] text-sage-800">
            <span>{full_name}</span>
            <RecommendedBadge
              isRecommended={is_recommended}
              recommendedAt={recommended_at}
              recommendedByName={recommended_by_display_name}
            />
          </h3>
          {title && <p className="mt-1 text-[12.5px] text-ink-soft">{title}</p>}
          {is_referral_partner && (
            <div className="mt-1.5">
              <Chip variant="affirm">Referral partner</Chip>
            </div>
          )}
        </div>
      </div>

      {displayOrgName && (
        <div className="mt-3 flex items-center gap-2 text-[13px] text-ink-soft">
          <Building2 size={14} aria-hidden="true" className="shrink-0 text-sage-300" />
          {/* UX-13 §3 never-silently rule: truncated names carry title. */}
          <span className="truncate" title={displayOrgName}>{displayOrgName}</span>
        </div>
      )}

      {specialties && (
        <p className="mt-2 line-clamp-2 text-xs text-ink-soft">{specialties}</p>
      )}

      <div className="mt-4 flex items-center justify-between border-t border-sage-800/[6%] pt-3">
        {displayPhone ? (
          <a
            href={`tel:${displayPhone.replace(/\D/g, '')}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-sage-600 hover:text-sage-800"
          >
            <Phone size={14} aria-hidden="true" />
            {formatPhone(displayPhone)}
          </a>
        ) : <span />}
        <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-sage-800">
          Contact
          <ArrowRight size={15} aria-hidden="true" className="transition-transform duration-[var(--dur-base)] ease-quiet group-hover:translate-x-0.5" />
        </span>
      </div>
    </div>
  )
}
